import { scopeIdsEqual } from '../../utils/scopeIdPlane';
import { normalizeResourceRef } from './resolveUserResourceProfileRef';

export type ResourceFilter = 'all' | `type:${string}` | `person:${string}`;

export interface ScopableItem {
  domain?: string;
  filterScope?: string;
  /** Filter / chip token (may be profile ref slug or short name). */
  res?: string;
  assignedResourceRef?: string;
  assignedResourceLabel?: string;
}

export interface ScopeContext {
  childrenByScope?: Record<string, string[]>;
  businessAreaScopes?: ReadonlySet<string>;
}

/**
 * Stable person identity for chip↔row matching.
 * Equates short names, profile refs, and people refs:
 * steve ≡ ai-op-resource-profile-steve ≡ co-prh-steve ≡ "Steve — Founder".
 */
export function assigneeIdentityKey(token: string | undefined): string | undefined {
  const bare = normalizeResourceRef(token);
  if (!bare) return undefined;
  let slug = bare.trim().toLowerCase();
  const prefixes = [
    'ai-op-resource-profile-',
    'ai-rp-',
    'ai-ddd-rp-',
    'co-prh-',
  ] as const;
  for (const prefix of prefixes) {
    if (slug.startsWith(prefix)) {
      slug = slug.slice(prefix.length);
      break;
    }
  }
  const pra = /^co-[a-z0-9]+-pra-(.+)$/i.exec(slug);
  if (pra?.[1]) {
    slug = pra[1];
  }
  // Display labels / multi-word names → leading given name (chip dedupe uses labels).
  if (/[\s—–\-|/]/.test(slug)) {
    slug = slug.split(/[\s—–\-|/]+/).find(Boolean) || slug;
  }
  return slug.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim() || undefined;
}

export function scopeKeys(scope: string, ctx?: ScopeContext): string[] {
  if (!scope || scope === '__all' || scope === 'company') {
    return [];
  }
  const children = ctx?.childrenByScope?.[scope];
  if (children?.length) {
    return [scope, ...children];
  }
  // Also resolve children when scope is a UUID-cased variant of a map key.
  if (ctx?.childrenByScope) {
    for (const [key, kids] of Object.entries(ctx.childrenByScope)) {
      if (scopeIdsEqual(key, scope) && kids.length) {
        return [scope, ...kids];
      }
    }
  }
  return [scope];
}

export function inScope(domain: string | undefined, scope: string, ctx?: ScopeContext): boolean {
  if (!scope || scope === '__all' || scope === 'company') {
    return true;
  }
  if (!domain) {
    return false;
  }
  const keys = scopeKeys(scope, ctx);
  if (keys.length === 0) return true;
  return keys.some((key) => scopeIdsEqual(key, domain));
}

/** True when a single assignment token matches a person:/type: resource chip. */
export function matchResource(itemRes: string | undefined, filter: ResourceFilter): boolean {
  if (!filter || filter === 'all') {
    return true;
  }
  if (!itemRes) {
    return false;
  }
  if (filter.startsWith('type:')) {
    const want = filter.slice(5).toLowerCase();
    return itemRes.trim().toLowerCase().startsWith(want);
  }
  if (filter.startsWith('person:')) {
    const want = normalizeResourceRef(filter)?.toLowerCase();
    const got = normalizeResourceRef(itemRes)?.toLowerCase();
    if (want && got && want === got) return true;
    const wantId = assigneeIdentityKey(want);
    const gotId = assigneeIdentityKey(got);
    return Boolean(wantId && gotId && wantId === gotId);
  }
  return true;
}

/**
 * Match a person/type chip against any assignment plane on the row
 * (res token, assignedResourceRef, display label).
 */
export function matchItemResource(
  item: Pick<ScopableItem, 'res' | 'assignedResourceRef' | 'assignedResourceLabel'>,
  filter: ResourceFilter
): boolean {
  if (!filter || filter === 'all') {
    return true;
  }
  if (filter.startsWith('type:')) {
    return matchResource(item.res, filter) || matchResource(item.assignedResourceRef, filter);
  }
  if (filter.startsWith('person:')) {
    const candidates = [item.res, item.assignedResourceRef, item.assignedResourceLabel];
    return candidates.some((c) => matchResource(c, filter));
  }
  return true;
}

export function passesFilter(
  item: ScopableItem,
  scope: string,
  resource: ResourceFilter,
  ctx?: ScopeContext
): boolean {
  const domain = item.domain ?? item.filterScope;
  return inScope(domain, scope, ctx) && matchItemResource(item, resource);
}

export function isBusinessAreaScope(scope: string, ctx?: ScopeContext): boolean {
  if (ctx?.businessAreaScopes) {
    return [...ctx.businessAreaScopes].some((s) => scopeIdsEqual(s, scope));
  }
  return false;
}
